import { useCallback, useEffect, useMemo, useState } from "react";

import { useHistory } from "react-router-dom";
import BackIcon from "@mui/icons-material/ArrowBackRounded";
import { Stack, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";

import { FilterTable } from "../../../Components/FilterTable/FilterTable";
import { FilterTableSkeleton } from "../../../Components/FilterTable/FilterTableSkeleton";
import { useCustomerId } from "../../../Components/hooks/useCustomerId";
import mixpanel from "../../../utils/mixpanel";
import { filters, headers } from "./Columns";
import { useLabels } from "./hooks";
import LabelDeleteDialog from "./LabelDeleteDialog";
import LabelDialog from "./LabelDialog";
import LabelRow from "./LabelRow";
import { type LabelRowItem } from "./types";
import { isSystemLabel } from "./utils";

export const LabelsBrowser = () => {
  const history = useHistory<any>();
  const customerId = useCustomerId();

  const [selectedLabel, setSelectedLabel] = useState<LabelRowItem>();
  const [labelDialogOpen, setLabelDialogOpen] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);

  const [labels, labelsLoading] = useLabels();

  const rows = useMemo(
    () =>
      labels
        ? labels
            .filter((label) => !isSystemLabel(label.data.type ?? ""))
            .map<LabelRowItem>((row) => ({ ...row, timesApplied: row.data.objects?.length ?? 0 }))
        : [],
    [labels]
  );

  useEffect(() => {
    mixpanel.track("analytics.labels.list");
  }, []);

  const handleBack = useCallback(() => {
    if (history.length > 0) {
      history.goBack();
    } else {
      history.push(`/customers/${customerId}/home`);
    }
  }, [history, customerId]);

  const labelRow = ({ row }) => (
    <LabelRow
      row={row}
      handleEdit={() => {
        setSelectedLabel(row);
        setLabelDialogOpen(true);
      }}
      handleDelete={() => {
        setSelectedLabel(row);
        setDeleteDialogOpen(true);
      }}
    />
  );

  const handleCreateLabel = useCallback(() => {
    setSelectedLabel(undefined);
    setLabelDialogOpen(true);
  }, []);

  if (labelsLoading) {
    return (
      <Box
        sx={{
          p: 1,
        }}
      >
        <FilterTableSkeleton />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "start",
      }}
    >
      <FilterTable<LabelRowItem>
        showRowsSelection={false}
        tableItems={rows}
        rowComponent={labelRow}
        headerColumns={headers}
        filterColumns={filters}
        filterBarPlaceholder="Filter"
        itemUniqIdentifierField="ref.id"
        defaultSortingColumnValue="data.timeCreated"
        toolbarProps={{
          primaryButton: {
            text: "Create label",
            onClick: handleCreateLabel,
            "data-cy": "new-label-button",
          },
          title: (
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
              <IconButton aria-label="Back" size="large" onClick={handleBack} data-cy="back-icon">
                <BackIcon fontSize="medium" color="action" />
              </IconButton>
              <Typography display="inline" variant="h1">
                Labels
              </Typography>
            </Stack>
          ),
        }}
      />
      {labelDialogOpen && (
        <LabelDialog
          selectedLabel={selectedLabel}
          open={labelDialogOpen}
          onClose={() => {
            setLabelDialogOpen(false);
          }}
        />
      )}
      {selectedLabel && deleteDialogOpen && (
        <LabelDeleteDialog
          labelId={selectedLabel.ref.id}
          open={deleteDialogOpen}
          onClose={() => {
            setDeleteDialogOpen(false);
          }}
        />
      )}
    </Box>
  );
};
